import * as _ from 'lodash';
import IZoneCombination = ZoneIQCalibration.IZoneCombination;
import ICalibrationUsesVal = ZoneIQCalibration.ICalibrationUsesVal;
import ICalibrationUsesLabel = ZoneIQCalibration.ICalibrationUsesLabel;
import { SubZoneModel } from './sub-zone.model';
import { ZoneModel } from './zone.model';
import { ZoneTypeModel } from './zone-type.model';
import { CalibrationUsesValModel } from '../calibration-uses-val.model';
import { CalibrationGeneralValue } from '../calibration-general-value.model';
import { CalibrationParkingValue } from '../calibration-parking-value.model';
import { CalibrationOfficeCommercialValue } from '../calibration-office-commercial-value.model';
import { CalibrationBonusValue } from '../calibration-bonus-value.model';
import { CalibrationSetModel } from '../calibration-set.model';
import { ZoningCodeModel } from '../..';
import IOverlay = ZoneIQCalibration.IOverlay;
import IZCmbApplicableTypologies = ZoneIQCalibration.IZCmbApplicableTypologies;
import { IZoneCombinationModel } from 'src/app/interfaces/zone-combination-model.interface';
import { ICalibrationParkingModel } from 'src/app/interfaces/calibration-parking-model.interface';
import { zoneCombinationMapperFactory } from 'src/app/mappers/zone-combination.mapper';
import { ICalibrationGeneralModel } from 'src/app/interfaces/calibration-general-model.interface';
import { ICalibrationOfficeCommercialModel } from 'src/app/interfaces/calibration-office-commercial-model.interface';
import { ICalibrationBonusModel } from 'src/app/interfaces/calibration-bonus-model.interface';
import ICalibrationSetAttributesStatistic = ZoneIQCalibration.ICalibrationSetAttributesStatistic;
import { CalibrationSetType } from 'src/shared/enums';
import IIsNotCalibratedReason = ZoneIQCalibration.IIsNotCalibratedReason;
import IAllowedUse = ZoneIQCalibration.IAllowedUse;
import IZCmbImportValidationResult = ZoneIQCalibration.IZCmbImportValidationResult;
import IZoneDescriptionImportValidationResult = ZoneIQCalibration.IZoneDescriptionImportValidationResult;
import IEntityTableFieldAttributeImportValidationResult =
    ZoneIQCalibration.IEntityTableFieldAttributeImportValidationResult;

const ZONE_COMBINATION_NAME_SEPARATOR = ' - ';

export class ZoneCombinationModel extends CalibrationSetModel<IZoneCombination> implements IZoneCombinationModel {
    Id: number;
    ZoneId: number;
    SubZoneId: number;
    ZoneTypeId: number;
    RevisionId: number;
    GridicsId: number;
    IsNotCalibrated: boolean;
    CalibrationErrorMessage: string;
    IsPlannedDevelopment: boolean;
    CloneId: number;
    BuildingTypologyMandatory: boolean;
    DefaultBuildingTypology: number;
    CreatedOn: Date;
    ModifiedOn: Date;
    BuildingTypologyIds: number[];
    BuildingTypologyData: IZCmbApplicableTypologies[];
    Description?: string;
    IsNotCalibratedReason?: IIsNotCalibratedReason[];

    Zone?: ZoneModel;
    SubZone?: SubZoneModel;
    ZoneType?: ZoneTypeModel;
    CalibrationUsesVals?: CalibrationUsesValModel[];
    CalibrationGeneral?: ICalibrationGeneralModel;
    CalibrationParking?: ICalibrationParkingModel;
    CalibrationOfficeCommercial?: ICalibrationOfficeCommercialModel;
    CalibrationBonus?: ICalibrationBonusModel;

    importValidationResult?:
        | IZCmbImportValidationResult
        | IZoneDescriptionImportValidationResult
        | IEntityTableFieldAttributeImportValidationResult;

    constructor(data: IZoneCombination) {
        const mapper = zoneCombinationMapperFactory();
        super(mapper.fromDTO(data));
        this._calibrationSetType = CalibrationSetType.ZONE_COMBINATION;
        /**
         * TODO :: BaseMutableModel has originalState and currentState so no need to assign this values to entity.
         * Also entity should not implement this interface and have this properties.
         * One reason why this properties should remain is eliminating currentState.
         * It will be replaced with using of entity properties.
         */
        Object.assign(this, mapper.fromDTO(data));

        // This condition because of back-end send some
        // BuildingTypologyMandatory of null, but we use boolean values on ECI ECI-223
        if (this.BuildingTypologyMandatory === null) {
            this.BuildingTypologyMandatory = false;
            this.origin.BuildingTypologyMandatory = false;
            this.currentState.BuildingTypologyMandatory = false;
        }
        this.CreatedOn = new Date(data.CreatedOn);
        this.ModifiedOn = new Date(data.ModifiedOn);
        this.importValidationResult = data.importValidationResult;
        this.initIdentifier();
    }

    get validateRequiredFields() {
        return !this.currentState.IsNotCalibrated;
    }

    get canSaveIsNotCalibrated(): boolean {
        return (
            !this.currentState.IsNotCalibrated ||
            (this.currentState.IsNotCalibrated && !_.isEmpty(this.currentState.IsNotCalibratedReason))
        );
    }

    static factory(
        data: IZoneCombination,
        zoningCode?: ZoningCodeModel,
        clbSetAttrStat?: ICalibrationSetAttributesStatistic[]
    ): ZoneCombinationModel {
        const zoneCombination = new ZoneCombinationModel(data);

        zoneCombination.initCalibrationUsesVals(
            data.CalibrationUsesVals,
            zoningCode.CalibrationUsesLabels,
            zoningCode.AllowedUses,
            zoningCode?.notAllowedUse?.Id
        );
        zoneCombination.CalibrationGeneral = new CalibrationGeneralValue(data.CalibrationGeneral);
        zoneCombination.CalibrationParking = new CalibrationParkingValue(data.CalibrationParking);
        zoneCombination.CalibrationOfficeCommercial = new CalibrationOfficeCommercialValue(
            data.CalibrationOfficeCommercial
        );
        zoneCombination.CalibrationBonus = new CalibrationBonusValue(data.CalibrationBonus);

        zoneCombination.Zone = zoningCode.Zones.find(zone => zone.Id === zoneCombination.ZoneId);
        zoneCombination.SubZone = zoningCode.SubZones.find(subZone => subZone.Id === zoneCombination.SubZoneId);
        zoneCombination.ZoneType = zoningCode.ZoneTypes.find(zoneType => zoneType.Id === zoneCombination.ZoneTypeId);
        zoneCombination.combineTypologiesData(zoningCode.Overlays);
        zoneCombination.setValidation();
        zoneCombination.initName();

        if (_.get(clbSetAttrStat, 'length')) {
            const attrStat = clbSetAttrStat.find(
                stat =>
                    stat.ParentId === zoneCombination.currentState.Id &&
                    stat.ParentType === CalibrationSetType.ZONE_COMBINATION
            );
            zoneCombination.hasComments = _.get(attrStat, 'hasComments', false);
            zoneCombination.hasLinks = _.get(attrStat, 'hasLinks', false);
            zoneCombination.hasAssumptions = _.get(attrStat, 'hasAssumptions', false);
        }

        return zoneCombination;
    }

    cancelChanges() {
        super.cancelChanges();
        if (this.isChanged) {
            this.removeChanges();
            this.setValidation();
        }
    }

    setValidation() {
        const currentState = this.currentState;
        this.isValidBuildingTypology =
            (currentState.BuildingTypologyMandatory &&
                currentState.DefaultBuildingTypology !== null &&
                this.isValidDefaultTypology()) ||
            !currentState.BuildingTypologyMandatory;
    }

    combineTypologiesData(Overlays: IOverlay[]) {
        if (_.isEmpty(this.BuildingTypologyIds)) {
            return;
        }
        this.BuildingTypologyIds.forEach((id: number) => {
            const coincidence = Overlays.find(overlay => overlay.Id === id);
            if (!this.BuildingTypologyData) {
                this.BuildingTypologyData = [];
            }
            if (coincidence) {
                this.BuildingTypologyData.push({
                    Name: coincidence.Name,
                    Id: id
                });
            }
        });

        if (this.BuildingTypologyData) {
            this.BuildingTypologyData.sort(ZoneCombinationModel.sortingCalibrationByName);
        }
    }

    isValidDefaultTypology(): boolean {
        return this.currentState.BuildingTypologyIds.indexOf(this.currentState.DefaultBuildingTypology) >= 0;
    }

    initCalibrationUsesVals(
        usesValsData: ICalibrationUsesVal[],
        usesLabels: ICalibrationUsesLabel[],
        allowedUses: IAllowedUse[],
        notAllowedUseId: number
    ) {
        super.initCalibrationUsesVals(usesValsData, usesLabels, allowedUses, notAllowedUseId, 'ZoneCombinationId');
    }

    deleteUseValue(useToDelete: ICalibrationUsesVal, notAllowedUseId?: number) {
        super.deleteUseValue(useToDelete, notAllowedUseId, 'ZoneCombinationId');
    }

    private getCombinationName(): string {
        const tokens = [];
        if (this.Zone) {
            tokens.push(this.Zone.Name);
        }
        if (this.SubZone) {
            tokens.push(this.SubZone.Name);
        }
        if (this.ZoneType) {
            tokens.push(this.ZoneType.Name);
        }
        return tokens.join(ZONE_COMBINATION_NAME_SEPARATOR);
    }

    initName() {
        this.Name = this.getCombinationName() || this.Name; // Fallback to existing Name if combination is incomplete
    }

    acceptChanges() {
        super.acceptChanges();
        Object.assign(this, this.origin);
    }
}
